/**
 * The console's action inbox: which envelope statuses belong in which of the four tiles.
 *
 * ux.md §6.1 calls the dashboard an action-needed inbox rather than a stats page, and the four
 * tiles have read "—" since they were laid out because the number behind them did not exist. The
 * placeholder was the honest choice at the time — `Dashboard.tsx` says so: "a dashboard that shows
 * zero for a figure it cannot know is lying in the most reassuring way possible". This module is
 * what makes the figures knowable.
 *
 * It lives in core rather than in the route because WHICH statuses mean "you need to do something"
 * is a product judgement, not a query detail: it is the kind of decision that should be reviewable
 * and testable on its own, and that two surfaces (the tiles now, a filtered list later) must agree
 * on. The database returns a faithful count per status; this decides what they mean.
 */

/**
 * Every status `ck_envelopes__status` admits (20260901007200).
 *
 * Held to the constraint by `packages/core/test/envelope-inbox.test.ts`, which fails if the check
 * gains a value this file does not place. A new status that silently landed in no bucket would go
 * missing from the dashboard without anything failing — the tiles would simply undercount, which is
 * the failure mode that is hardest to notice and easiest to disbelieve.
 */
export const ENVELOPE_STATUSES = [
  'draft',
  'sent',
  'in_progress',
  'correcting',
  'completed',
  'sealed',
  'declined',
  'expired',
  'voided',
] as const

export type EnvelopeStatus = (typeof ENVELOPE_STATUSES)[number]

export type InboxBucket = 'needs_you' | 'waiting' | 'drafts' | 'completed'

/**
 * The mapping, stated once.
 *
 * `needs_you` is the sender's stuck pile: an envelope that STOPPED and cannot move again until a
 * person decides something. A decline needs a decision (re-send, or let it go), an expiry needs a
 * new link, and a correction is by definition mid-edit.
 *
 * These four tiles count only what you SENT, and that is still true. The paragraph that used to sit
 * here said matching a signer by address would be "a guess dressed as a fact", wrong for an alias, a
 * plus-address or a shared mailbox — and for what this function does, it would be. It counts rows
 * for a tile with nothing verifying that the person reading it controls the address.
 *
 * P7-08 added the other half, and it is a different question with a different answer.
 * `app_private.inbox_for_me` matches a recipient's address against the caller's CONFIRMED account
 * email — and confirmation is precisely the proof of control the objection said was missing: GoTrue
 * sent a link to that address and somebody clicked it. So the in-app door is exactly as strong as
 * the emailed one, because both rest on control of the mailbox and neither on anything else.
 *
 * The three awkward cases land where they should. An alias or a plus-address that does not equal the
 * account email simply does not appear in the inbox — a false negative, and harmless, because the
 * emailed link still arrives. A shared mailbox does let anyone holding a confirmed account at that
 * address open the ceremony, which sounds alarming until you notice the invitation email already
 * lands in that same shared mailbox: the in-app door grants nothing the mailbox did not already
 * grant. What would have been a guess is attributing an envelope to somebody; what this does is
 * authorise somebody who has proved they are the addressee.
 *
 * `voided` is in no bucket ON PURPOSE and is the one status that is not counted anywhere. A void is
 * finished and needs nothing: it is not work, and it is not an achievement. Putting it under
 * `completed` would inflate the number a customer is most likely to read as "how much did we get
 * done"; putting it under `needs_you` would ask them to act on something already resolved.
 */
const BUCKET_OF: Record<EnvelopeStatus, InboxBucket | null> = {
  draft: 'drafts',
  sent: 'waiting',
  in_progress: 'waiting',
  correcting: 'needs_you',
  completed: 'completed',
  sealed: 'completed',
  declined: 'needs_you',
  expired: 'needs_you',
  voided: null,
}

export type InboxCounts = {
  readonly needs_you: number
  readonly waiting: number
  readonly drafts: number
  readonly completed: number
}

const ZERO: InboxCounts = { needs_you: 0, waiting: 0, drafts: 0, completed: 0 }

export function isEnvelopeStatus(value: string): value is EnvelopeStatus {
  return (ENVELOPE_STATUSES as readonly string[]).includes(value)
}

/**
 * Fold per-status counts into the four tiles.
 *
 * An UNKNOWN status is dropped rather than guessed at, and the caller is told how many rows that
 * cost. The alternative — quietly folding it into one of the four — would mean a status added by a
 * migration ahead of this deployment silently changed a number a customer reads, with nothing
 * anywhere saying so. `unknown` being non-zero is a deployment-order fact worth logging, not an
 * error worth failing a dashboard for.
 */
export function bucketEnvelopeCounts(
  counts: readonly { readonly status: string; readonly count: number }[],
): InboxCounts & { readonly unknown: number } {
  const out = { ...ZERO, unknown: 0 }
  for (const { status, count } of counts) {
    if (!Number.isFinite(count) || count < 0) continue
    if (!isEnvelopeStatus(status)) {
      out.unknown += count
      continue
    }
    const bucket = BUCKET_OF[status]
    if (bucket === null) continue
    out[bucket] += count
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// The inbox SCREEN: five tabs over both sides of a person's work.
//
// The four tiles above answer "how is my sending going". These answer a different question — "where
// do I go now" — over a list that unions two sources with different authorization models:
//
//   * what you SENT, read from `envelopes` under ordinary RLS, scoped to your organization;
//   * what was sent TO YOU, read from `public.my_inbox()`, scoped to your confirmed address and
//     deliberately crossing organizations, because a recipient usually is not a member of the
//     sender's tenant.
//
// Keeping one tab vocabulary over both is the point. A person does not think "this is a sent-side
// waiting and that is a received-side waiting"; they think "I am waiting on someone else".

/** What the database's `bucket` column says about an envelope sent TO you (20260901017900). */
export const RECEIVED_BUCKETS = ['action_required', 'waiting', 'completed', 'closed'] as const
export type ReceivedBucket = (typeof RECEIVED_BUCKETS)[number]

export function isReceivedBucket(value: string): value is ReceivedBucket {
  return (RECEIVED_BUCKETS as readonly string[]).includes(value)
}

/**
 * The seven tabs, in the order they are shown.
 *
 * FIVE ARE A STATUS the merge assigns (`tabOfSentEnvelope`, `tabOfReceivedBucket`). `drafts` cannot
 * appear on the received side at all — a draft has been shown to nobody, and `inbox_for_me` excludes
 * it for exactly that reason — so it is sent-only, which is why the received vocabulary above has
 * four values and the status tabs have five.
 *
 * TWO ARE SHELVES and no status ever maps to either: `archived` and `deleted`. They were one tab
 * labelled "Deleted / Archived" when the shelf arrived (founder's point 8, 2026-09-13) and are two
 * now, because the founder is right that they are separate things — archiving is filing something
 * you mean to keep, and deleting is putting it in the bin to be purged. The sidebar has always shown
 * Archive and Trash as separate places, so one combined tab was the odd surface out.
 *
 * The shelf names differ from the tab names by one word and that is deliberate rather than sloppy:
 * the tab is `deleted` because that is what the badge and the sidebar call it to a reader, and the
 * API's filter and the row's shelf are `trashed` because that is the column. `SHELF_OF_TAB` below is
 * the one place the two vocabularies meet, so nothing else has to know.
 *
 * The inbox reads each shelf from the list API's own filter and reads the five status tabs with
 * `archived=false`, so an envelope appears in exactly one tab.
 */
export const INBOX_TABS = [
  'action_required',
  'waiting',
  'drafts',
  'completed',
  'closed',
  'archived',
  'deleted',
] as const
export type InboxTab = (typeof INBOX_TABS)[number]

/** A shelf tab, and the `shelf` value its rows carry. The only place the two words meet. */
export const SHELF_OF_TAB = { archived: 'archived', deleted: 'trashed' } as const
export type ShelfTab = keyof typeof SHELF_OF_TAB
export type Shelf = (typeof SHELF_OF_TAB)[ShelfTab]

export function isShelfTab(tab: InboxTab): tab is ShelfTab {
  return tab === 'archived' || tab === 'deleted'
}

/**
 * A sent envelope's tab.
 *
 * Precedence, not preference: a terminal envelope is filed under how it ended whatever else is true
 * of it, and only then do the live states get a say. This is deliberately NOT `BUCKET_OF` above —
 * that map puts `declined` and `expired` under `needs_you`, because a stuck envelope is work, and
 * it is right about that for a COUNT. A list is a different instrument: somebody looking for the
 * agreement that fell over goes to a tab named for what happened to it, and finding it filed under
 * "action required" alongside live work is how a list stops being scannable. The two surfaces
 * disagree because they are answering different questions, and this comment is here so that reads
 * as a decision rather than a drift.
 *
 * `voided` is filed under `closed` here, though `BUCKET_OF` counts it nowhere. Same reason in
 * reverse: excluding it from a TILE keeps a headline number honest, but excluding it from a LIST
 * would mean an envelope you voided is nowhere at all, which reads as data loss.
 *
 * `correcting` is the only sent status that asks for something: it is mid-edit and cannot move
 * until the sender finishes.
 */
export function tabOfSentEnvelope(status: EnvelopeStatus): InboxTab {
  switch (status) {
    case 'declined':
    case 'expired':
    case 'voided':
      return 'closed'
    case 'completed':
    case 'sealed':
      return 'completed'
    case 'draft':
      return 'drafts'
    case 'correcting':
      return 'action_required'
    case 'sent':
    case 'in_progress':
      return 'waiting'
  }
}

/**
 * A received envelope's tab. The database has already decided this — `inbox_for_me` evaluates the
 * routing to know whether it is your turn, and the browser cannot and must not re-derive that — so
 * this is a rename, not a judgement, and it exists only so both sides speak one vocabulary.
 */
export function tabOfReceivedBucket(bucket: ReceivedBucket): InboxTab {
  return bucket
}

export type TabCounts = Readonly<Record<InboxTab, number>>

export const ZERO_TAB_COUNTS: TabCounts = {
  action_required: 0,
  waiting: 0,
  drafts: 0,
  completed: 0,
  closed: 0,
  archived: 0,
  deleted: 0,
}

export function countByTab(tabs: readonly InboxTab[]): TabCounts {
  const out: Record<InboxTab, number> = { ...ZERO_TAB_COUNTS }
  for (const t of tabs) out[t] += 1
  return out
}

/**
 * The public API's wire form of an envelope id, reduced to the storage form.
 *
 * `api-platform.md` §1.2 gives every resource a typed prefix, and `routes/envelopes.ts` serializes
 * the id with an `env_` prefix on the way out. PostgREST does not — `my_inbox()` returns the column
 * itself, which is a bare uuid. So the same envelope arrives prefixed from one source and bare from
 * the other, and anything comparing the two without this matches nothing at all.
 *
 * It did. The dedup below shipped keyed on the raw values, had no effect whatsoever, and looked
 * exactly like a deploy that had not landed yet — because the failure mode of a prefix mismatch is
 * SILENCE, not an error. Same class as the `sgn_`/`sig_` split already recorded against the signers
 * routes, and the second time it has cost a debugging session.
 *
 * Deliberately narrow: it strips ONE known prefix rather than everything before the first
 * underscore, so an id shaped unexpectedly is compared as-is instead of being quietly truncated
 * into a collision with something else.
 */
export function bareEnvelopeId(id: string): string {
  return id.startsWith('env_') ? id.slice('env_'.length) : id
}

export type InboxRow<R, S> =
  | { readonly side: 'received'; readonly tab: InboxTab; readonly item: R }
  | { readonly side: 'sent'; readonly tab: InboxTab; readonly item: S }

/**
 * Fold the two sources into one list, one row per envelope.
 *
 * They OVERLAP, and only for some people: anyone who is both a recipient and able to read the
 * sending organization's envelopes — an owner or an admin, who holds `envelopes.read_all_org`. A
 * plain member never sees the collision, which is what makes it the kind of defect that ships. It
 * did: measured on the Acme demo, the CEO's "Waiting on others" tab read 6 for three envelopes,
 * because each appeared once as something sent to them and once as something their organization
 * had out.
 *
 * The RECEIVED side claims the envelope, and that is the substantive half of this function. A sent
 * row knows the envelope is `in_progress`; a received row knows whether it is waiting on YOU — and
 * `my_turn` is not recoverable from the other side at all, because deciding it means evaluating the
 * routing across every other recipient, which no browser can see or be trusted to compute. Dropping
 * the row that carries less is safe; dropping the one that carries more would silently turn
 * "your turn" into "in progress" for precisely the senior people who are on both sides of it.
 */
export function mergeInbox<
  R extends { readonly envelope_id: string; readonly bucket: ReceivedBucket },
  S extends { readonly id: string; readonly status: string },
>(received: readonly R[], sent: readonly S[]): ReadonlyArray<InboxRow<R, S>> {
  const out: Array<InboxRow<R, S>> = []
  const claimed = new Set<string>()
  for (const item of received) {
    claimed.add(item.envelope_id)
    out.push({ side: 'received', tab: tabOfReceivedBucket(item.bucket), item })
  }
  for (const item of sent) {
    // A status this build does not know is dropped rather than guessed at, exactly as
    // `bucketEnvelopeCounts` drops one: a row in no tab is invisible, and a row in the WRONG tab is
    // worse, because somebody acts on it.
    if (!isEnvelopeStatus(item.status)) continue
    if (claimed.has(bareEnvelopeId(item.id))) continue
    out.push({ side: 'sent', tab: tabOfSentEnvelope(item.status), item })
  }
  return out
}
